import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Chip,
} from '@mui/material';
import { Client } from '../types/client';
import { US_STATES, STATE_NAMES } from '../constants/states';

interface StateRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  clients: Client[];
}

// Define a type for US state codes
type StateCode = keyof typeof STATE_NAMES;

export const StateRegistrationModal: React.FC<StateRegistrationModalProps> = ({
  open,
  onClose,
  onSubmit,
  clients
}) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedStates, setSelectedStates] = useState<StateCode[]>([]);

  console.log('Clients received:', clients);

  // Convert readonly array to regular array
  const stateOptions = [...US_STATES] as StateCode[];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: '#1A202C',
          borderRadius: '8px',
          maxWidth: 'none'
        },
      }}
    >
      <DialogTitle sx={{ color: '#FFFFFF', fontSize: '24px' }}>
        New State Registration
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          width: '100%',
          minWidth: '500px' 
        }}>
          <Autocomplete
            value={selectedClient}
            onChange={(_, newValue) => {
              console.log('Selected client:', newValue);
              setSelectedClient(newValue);
            }}
            options={clients || []}
            getOptionLabel={(option) => option?.name || ''}
            noOptionsText={<span style={{ color: '#1976d2' }}>No options</span>}
            sx={{
              '& .MuiAutocomplete-noOptions': {
                color: '#1976d2 !important'
              },
              '& .MuiAutocomplete-option': {
                color: '#1976d2 !important'
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Client *"
                sx={{
                  backgroundColor: '#1E2A3B',
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#FFFFFF'
                  }
                }}
              />
            )}
          />

          <Autocomplete<StateCode, true>
            multiple
            value={selectedStates}
            onChange={(_, newValue) => setSelectedStates(newValue)}
            options={stateOptions}
            getOptionLabel={(state) => STATE_NAMES[state]}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={STATE_NAMES[option]}
                  sx={{
                    backgroundColor: 'transparent',
                    border: '1px solid #4FD1C5',
                    color: '#4FD1C5',
                    '& .MuiChip-deleteIcon': {
                      color: '#4FD1C5',
                      '&:hover': {
                        color: '#38B2AC'
                      }
                    }
                  }}
                />
              ))
            }
            renderOption={(props, option) => (
              <li {...props} style={{ color: '#4FD1C5' }}>
                {STATE_NAMES[option]}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select States *"
                sx={{
                  backgroundColor: '#1E2A3B',
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#FFFFFF'
                  }
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: '#3182CE',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          CANCEL
        </Button>
        <Button 
          variant="contained"
          onClick={() => onSubmit({ clientId: selectedClient?.id || '', states: selectedStates })}
          sx={{ 
            bgcolor: '#1976d2',
            '&:hover': {
              bgcolor: '#1565c0'
            },
            textTransform: 'none',
            borderRadius: '4px',
            px: 2
          }}
        >
          CREATE REGISTRATION
        </Button>
      </DialogActions>
    </Dialog>
  );
};